/**
 * Delivery Timer JavaScript
 * Handles countdown timer with timezone support for delivery information
 */

class DeliveryTimer {
  constructor() {
    this.init();
  }

  init() {
    this.deliveryElements = document.querySelectorAll("[data-delivery-info]");
    if (this.deliveryElements.length > 0) {
      this.startTimers();
    }
  }

  startTimers() {
    this.deliveryElements.forEach((element) => {
      this.initTimer(element);
    });
  }

  initTimer(element) {
    const timezone = element.dataset.timezone || "America/New_York";
    const cutoffTime = parseInt(element.dataset.cutoffTime) || 15;
    const blackoutDates = element.dataset.blackoutDates || "";

    // Start countdown
    this.updateCountdown(element, timezone, cutoffTime, blackoutDates);

    // Update every second
    setInterval(() => {
      this.updateCountdown(element, timezone, cutoffTime, blackoutDates);
    }, 1000);
  }

  updateCountdown(element, timezone, cutoffTime, blackoutDates) {
    const now = this.getTimeInTimezone(timezone);
    const currentTime = now.getHours();

    // Calculate cutoff time for today or tomorrow
    let cutoffDateTime = new Date(now);
    cutoffDateTime.setHours(cutoffTime, 0, 0, 0);

    // If current time is past cutoff, use tomorrow's cutoff
    if (currentTime >= cutoffTime) {
      cutoffDateTime.setDate(cutoffDateTime.getDate() + 1);
    }

    // Calculate time remaining
    const timeRemaining = cutoffDateTime - now;

    if (timeRemaining > 0) {
      this.displayCountdown(element, timeRemaining);
    } else {
      // Cutoff has passed, reset to tomorrow
      const tomorrowCutoff = new Date(now);
      tomorrowCutoff.setDate(tomorrowCutoff.getDate() + 1);
      tomorrowCutoff.setHours(cutoffTime, 0, 0, 0);
      this.displayCountdown(element, tomorrowCutoff - now);
    }
  }

  getTimeInTimezone(timezone) {
    try {
      const now = new Date();
      const options = {
        timeZone: timezone,
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: false,
      };

      const formatter = new Intl.DateTimeFormat("en-US", options);
      const parts = formatter.formatToParts(now);

      const year = parseInt(parts.find((p) => p.type === "year").value);
      const month = parseInt(parts.find((p) => p.type === "month").value) - 1;
      const day = parseInt(parts.find((p) => p.type === "day").value);
      const hour = parseInt(parts.find((p) => p.type === "hour").value);
      const minute = parseInt(parts.find((p) => p.type === "minute").value);
      const second = parseInt(parts.find((p) => p.type === "second").value);

      return new Date(year, month, day, hour, minute, second);
    } catch (error) {
      console.warn("Error getting timezone time, using local time:", error);
      return new Date();
    }
  }

  displayCountdown(element, milliseconds) {
    const timerElement = element.querySelector("[data-countdown-timer]");
    if (!timerElement) return;

    const hoursElement = timerElement.querySelector(".countdown-hours");
    const minutesElement = timerElement.querySelector(".countdown-minutes");
    const secondsElement = timerElement.querySelector(".countdown-seconds");

    if (!hoursElement || !minutesElement || !secondsElement) return;

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    hoursElement.textContent = this.padZero(hours);
    minutesElement.textContent = this.padZero(minutes);
    secondsElement.textContent = this.padZero(seconds);
  }

  padZero(number) {
    return number.toString().padStart(2, "0");
  }

  // Helper method to check if a date is a blackout date
  isBlackoutDate(date, blackoutDates) {
    if (!blackoutDates) return false;

    const dateStr = this.formatDateForComparison(date);
    const blackoutArray = blackoutDates.split(",").map((date) => date.trim());

    return blackoutArray.includes(dateStr);
  }

  formatDateForComparison(date) {
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  // Helper method to check if date is weekend (Sunday = 0, Monday = 1)
  isRestrictedDay(date) {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 1; // Sunday or Monday
  }

  // Method to calculate next available delivery date (for future enhancements)
  calculateNextDeliveryDate(timezone, cutoffTime, blackoutDates) {
    const now = this.getTimeInTimezone(timezone);
    const currentHour = now.getHours();

    let daysToAdd = currentHour < cutoffTime ? 1 : 2;
    let deliveryDate = new Date(now);
    deliveryDate.setDate(deliveryDate.getDate() + daysToAdd);

    // Find next available delivery date
    while (
      this.isRestrictedDay(deliveryDate) ||
      this.isBlackoutDate(deliveryDate, blackoutDates)
    ) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }

    return deliveryDate;
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new DeliveryTimer();
});

// Also initialize for dynamic content (Shopify sections)
if (window.Shopify && Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    new DeliveryTimer();
  });
}
